import { Button, FormControl, FormControlLabel, FormHelperText, FormLabel, Radio, RadioGroup, TextField } from '@mui/material';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductActionReasonType } from 'types/product.types';
import { notification } from 'utils/snackbar';

type Props = {
  opened: boolean;
  onClose: () => void;
  onAction: (obj: { reason: string; comment: string; reasonType: string }) => void;
  reasonService: () => Promise<ProductActionReasonType[]>;
  refreshParentDataFn: () => void;
  translationStrings: {
    actionButton: string;
    actionSuccess: string;
    actionNoReasonError: string;
  };
};

const AdminProductActionModal = ({
  opened,
  onClose,
  onAction,
  reasonService,
  refreshParentDataFn,
  translationStrings: { actionButton, actionSuccess, actionNoReasonError },
}: Props) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [reason, setReason] = useState<ProductActionReasonType>({ name: null, type: null });
  const [comment, setComment] = useState<string>('');
  const [reasons, setReasons] = useState<ProductActionReasonType[]>(undefined);
  const [validation, setValidation] = useState<boolean>(true);

  const reset = () => {
    setValidation(true);
    setReason({ name: null, type: null });
    setComment('');
  };

  const handleAction = async () => {
    setValidation(true);

    if (!reason?.type) {
      setValidation(false);

      return;
    }

    try {
      setIsLoading(true);

      await onAction({
        reason: reason.name,
        reasonType: reason.type,
        comment,
      });

      if (typeof refreshParentDataFn === 'function') {
        refreshParentDataFn();
      }

      setIsLoading(false);
      notification.showSuccessNotification(t(actionSuccess));
    } catch (err) {
      setIsLoading(false);
      console.error(err);
    } finally {
      handleClose();
    }
  };

  const loadReasons = async () => {
    setIsLoading(true);

    const loadedReasons = await reasonService();

    if (loadedReasons) {
      setReasons(loadedReasons);
    }
    setIsLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (opened && !reasons?.length) {
      loadReasons();
    }
  }, [opened]);

  return (
    <>
      <Modal
        open={opened}
        onClose={() => handleClose()}
        className="product-action-dialog"
        title="components.admin.product.reason.title"
        titleUnderline={false}
        actionsTopBorderline={false}
        closeButtonVisible={false}
        closeIconVisible={false}
        actions={[
          <div key="modal-action-button">
            <Button
              style={{
                textTransform: 'none',
                marginRight: '8px',
              }}
              variant="contained"
              color="primary"
              onClick={() => handleAction()}
            >
              {t(actionButton)}
            </Button>
            <Button
              style={{
                textTransform: 'none',
              }}
              variant="outlined"
              color="primary"
              onClick={() => handleClose()}
            >
              {t('common.cancel')}
            </Button>
          </div>,
        ]}
      >
        <div className="product-action-dialog__content-wrapper mobile-full-width">
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {/* ACTION REASON */}
              <FormControl error={!validation}>
                <FormLabel component="legend">{t('components.admin.product.reason.why')}</FormLabel>
                <RadioGroup value={reason.type}>
                  {reasons?.map((radioReason) => (
                    <FormControlLabel
                      key={radioReason.type}
                      className="product-action-dialog__radio-control"
                      control={<Radio color="primary" size="small" />}
                      value={radioReason.type}
                      label={radioReason.name}
                      onChange={() => {
                        setValidation(true);
                        setReason(radioReason);
                      }}
                    />
                  ))}
                </RadioGroup>
                {!validation && <FormHelperText>{t(actionNoReasonError)}</FormHelperText>}
              </FormControl>

              {/* ACTION COMMENT */}
              <TextField
                variant="standard"
                id="action-comment"
                className="action-comment"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                margin="normal"
                multiline={true}
                InputLabelProps={{
                  shrink: true,
                }}
                label={t('components.admin.product.reason.description-optional')}
              />
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default AdminProductActionModal;
